import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { scaleLinear, scaleUtc } from 'd3-scale';
import { getD3Formatter } from '../languages'
import { timeWeek } from 'd3-time';
import { extent, max, mean, deviation, quantile } from 'd3-array';
import { line, curveCatmullRom, area } from 'd3-shape'
import { getText } from '../languages'

import './style.scss';

const minWidth = 640;
const baseWidth = 960;
const baseHeight = 480;

const hideAverageLineMinPopulation = 100000 // this is referenced in the About copy so if changing, update About copy

const padding = {
  top: 64,
  bottom: 36,
  left: 8,
  right: 64,
};

const defaultText = {
  large: 16,
  medium: 12,
  week: 12,
  small: 9,
};
const smallText = {
  large: 14,
  medium: 12,
  week: 9,
  small: 9,
};

function formatDate(date, language, year = false) {
  let options = { month: 'numeric', day: 'numeric' }
  if (year) {
    options.year = 'numeric'
  }
  const format = new Intl.DateTimeFormat(language, options).format;
  return format(date)
}

function splitToTwoStrings(input) {
  const words = input.split(' ')
  const totalLength = input.length;
  let line1 = ''
  let line2 = ''
  let secondLine = false
  while(words.length > 0) {
    const nextWord = words.shift();
    if (!secondLine && line1.length + nextWord.length > totalLength / 2) {
      secondLine = true
    }
    if (secondLine) {
      line2 += nextWord + ' '
    } else {
      line1 += nextWord + ' '
    }
  }
  return [line1.trim(), line2.trim()]
}

export default function BarGraph(props) {
  const [tooltipData, setTooltipData] = useState(null);
  const [dimensions, setDimensions] = useState({ width: baseWidth, height: baseHeight, barWidth: 16, scale: 1 });
  const [text, setText] = useState(defaultText);
  const [firstConfirmedTextWidth, setFirstConfirmedTextWidth] = useState(null);
  const graphRef = useRef();

  const { contentWidth, contentHeight, regionType, stateData, selectedMetric, selectedDatum, language, directionality } = props;
  const isRTL = directionality === 'rtl';
  const { metricKey } = selectedMetric;

  const { formatNumber, formatPercentage, formatThousands } = useMemo(() => {
    const format = getD3Formatter(language);
    const formatNumber = format(',');
    const formatPercentage = format('.0%');
    const formatThousands = format('~s');
    return { formatNumber, formatPercentage, formatThousands };
  }, [language]);

  const { data, outlierData } = useMemo(() => {
    let data = [];
    if (regionType === 'world') {
      data = stateData['world'].data.slice();
    } else if (regionType === 'usa') {
      data = stateData['US'].data.slice();
    } else if (regionType === 'state' && selectedDatum) {
      data = stateData[selectedDatum.stateAbbr].data.slice();
    } else if (regionType === 'county' && selectedDatum) {
      data = selectedDatum.data.slice();
    } else if (regionType === 'country' && selectedDatum) {
      data = selectedDatum.data.slice();
    }

    // determine if first value is outlier
    let firstValueIsOutlier = false;
    let outlierData = null;
    const [firstDatum] = data;
    if (firstDatum) {
      // just using cases for now
      // const firstValue = firstDatum[`new_${metricKey}`];
      // const allValues = data.map(d => d[`new_${metricKey}`]);
      const firstValue = firstDatum[`new_cases`];
      const allValues = data.map(d => d[`new_cases`]);
      const standardDeviation = deviation(allValues);
      const valuesExcludingFirst = allValues.filter(d => d !== firstValue);
      const quantileBound = quantile(valuesExcludingFirst, 0.95); // 95th percentile
      // const largestValueExcludingFirst = max(valuesExcludingFirst);
      // firstValueIsOutlier = firstValue > largestValueExcludingFirst + standardDeviation;
      firstValueIsOutlier = firstValue > quantileBound + standardDeviation;
      if (firstValueIsOutlier) {
        // set values to display in flag
        outlierData = {
          cases: firstDatum[`new_cases`],
          deaths: firstDatum[`new_deaths`],
        };
        // remove first item from data
        data.splice(0, 1);
      }
    }

    data = data.map((datum, index) => {
      // recalculate 7 day trend
      if (firstValueIsOutlier) {
        const lastSevenDays = data.slice(Math.max(0, index - 7 + 1), index + 1)
        datum.new_cases_7dayAverage = mean(lastSevenDays, d => d.new_cases)
        datum.new_deaths_7dayAverage = mean(lastSevenDays, d => d.new_deaths)
      }
      //
      datum.priorWeek = data[index - 7] || null;
      return datum;
    });

    return { data, outlierData };
  }, [regionType, stateData, selectedDatum]);

  useEffect(() => {
    const width = contentWidth >= minWidth ? contentWidth : minWidth;
    const scale = contentWidth >= minWidth ? 1 : contentWidth / minWidth;
    if (width < baseWidth) {
      setText(smallText);
    } else {
      setText(defaultText);
    }
    const height = contentHeight
      ? contentHeight / scale
      : width / baseWidth * baseHeight;
    const dataLength = data.length || 30;
    const barWidth = (contentWidth / dataLength) / 2;
    setDimensions({ width, height, barWidth, scale });
  }, [contentWidth, contentHeight, data.length]);

  const xExtent = useMemo(() => (data.length ? extent(data.map(day => day.date)) : [new Date('Jan 01 2019'), new Date()]), [data]);
  const xScale = useMemo(() => (
    scaleUtc()
      .domain(xExtent)
      .range([padding.left + dimensions.barWidth / 2, dimensions.width - (dimensions.barWidth / 2 + padding.right)])
  ), [xExtent, dimensions.width, dimensions.barWidth]);
  const maxLabelWidth = 26;
  const xTicks = useMemo(() => (
    [
      ...new Set(
        timeWeek.every(2).range(...xExtent)
          .filter(date => {
            const xPos = xScale(date);
            if (xPos - maxLabelWidth < xScale(xExtent[0])) {
              return false;
            }
            if (xPos + maxLabelWidth > xScale(xExtent[1])) {
              return false;
            }
            return true;
          })
          .concat(xExtent)
      )
    ].map(xt => {
      const formatedDate = formatDate(xt, language);
      const lineHeight = formatedDate === formatDate(xScale.domain()[1], language)
        ? padding.bottom / 2
        : padding.bottom / 4;
      return (
        <g key={formatedDate} transform={`translate(${xScale(xt)}, 0)`}>
          <line y2={lineHeight} stroke='#707070' />
          <text
            fontSize={text.medium}
            dy={padding.bottom - 2}
            textAnchor='middle'
          >
            {formatedDate}
          </text>
        </g>
      );
    })
  ), [xScale, xExtent, language, text.medium]);

  const yMax = useMemo(() => max(data.map(day => day[`new_${metricKey}`]).concat(1)), [data, metricKey]);
  const yScale = useMemo(() => (
    scaleLinear()
      .domain([0, yMax])
      .range([0, yMax ? dimensions.height - (padding.top + padding.bottom) : 0])
  ), [yMax, dimensions.height]);

  const yTicks = useMemo(() => (
    yScale.ticks(5).filter(yt => Number.isInteger(yt))
      .map(yt => {
        const lineStroke = yt === 0 ? '#707070' : '#C4C4C4';
        return (
          <g key={yt} transform={`translate(0, ${-yScale(yt)})`}>
            <line x2={dimensions.width - padding.right} stroke={lineStroke} />
            <text
              transform={`translate(${dimensions.width - padding.right / 3 * 2}, 0)`}
              fontSize={text.medium}
              dy={text.medium / 4}
            >
              {formatThousands(yt)}
            </text>
          </g>
        );
      })
  ), [yScale, dimensions.width, formatThousands, text.medium]);

  const hoverDay = useMemo(() => (
    function(event, day) {
      let x = 0,
          y = 0,
          transform = null,
          percentChange = null;
      if ('clientX' in event) {
        const graphPosition = graphRef.current.getBoundingClientRect();
        x = event.clientX - graphPosition.x;
        y = event.clientY - graphPosition.y;
        transform = x > graphPosition.width / 2
          ? `translate(-110%, -100%)`
          : `translate(10%, -100%)`;
        if (day.priorWeek) {
          percentChange = (day[`new_${metricKey}`] - day.priorWeek[`new_${metricKey}`]) / day.priorWeek[`new_${metricKey}`];
        }
      }
      setTooltipData({ position: { x, y, transform }, data: day, percentChange });
    }
  ), [metricKey]);

  const sevenDayAverageGroup = useMemo(() => {
    const lineGen = line()
      .x(d => xScale(d.date))
      .y(d =>  Math.max(0, dimensions.height - padding.bottom - yScale(d[`new_${metricKey}_7dayAverage`])))
      .curve(curveCatmullRom.alpha(0.5));
    const areaGen = area()
      .x(d => xScale(d.date))
      .y1(d =>  Math.max(0, dimensions.height - padding.bottom - yScale(d[`new_${metricKey}_7dayAverage`])))
      .y0(dimensions.height - padding.bottom)
      .curve(curveCatmullRom.alpha(0.5));

    let sevenDayAverageLinePath = null;
    let sevenDayAverageAreaPath = null;
    let sevenDayAverageText = null;

    if (props.population >= hideAverageLineMinPopulation) {
      sevenDayAverageLinePath = lineGen(data)
      sevenDayAverageAreaPath = areaGen(data)
      sevenDayAverageText = (
        <text y={padding.top - 10} fontSize={9} dx={isRTL ? dimensions.width : 0}>
          {getText(language, metricKey === 'cases' ? 'line7dayAvgNewCases' : 'line7dayAvgNewFatalties')}
        </text>
      );
    }

    const averageStroke = metricKey === 'cases'
        ? '#883143'
        : '#272828';
    const averageFill = metricKey === 'cases'
        ? 'rgba(136, 49, 67, 0.1)'
        : 'rgba(140, 153, 154, 0.1)';

    return (
      <g id='sevenDayAverage'>
        <path className='noPointer' d={sevenDayAverageLinePath} stroke={averageStroke} strokeWidth={5} opacity={0.85} fill='none' />
        <path className='noPointer' d={sevenDayAverageAreaPath} stroke='none' fill={averageFill} />
        {sevenDayAverageText}
      </g>
    );
  }, [data, dimensions.height, dimensions.width, isRTL, language, metricKey, props.population, xScale, yScale]);

  const firstConfirmedTextRef = useCallback(ref => {
    if (ref) {
      const dimensions = ref.getBoundingClientRect()
      if (dimensions.width !== firstConfirmedTextWidth) {
        setFirstConfirmedTextWidth(dimensions.width)
      }
    }
  /* necessary so that width changes when language changes */
  // eslint-disable-next-line
  }, [language, firstConfirmedTextWidth, metricKey, outlierData]);

  const firstCase = useMemo(() => {
    if (data[0]) {
      const hasOutlier = outlierData !== null;
      const firstConfirmedText = hasOutlier
        ? `${formatNumber(outlierData[metricKey])} ${getText(language, `reported ${metricKey} before`)} ${formatDate(data[0].date, language)}`
        : getText(language, 'firstReportedCase');
      let rectFill = 'white';
      if (hasOutlier) {
        rectFill = metricKey === 'cases'
          ? '#f3eaec'
          : '#f3f5f5';
      }
      const firstConfirmedLines = splitToTwoStrings(firstConfirmedText);
      const firstCaseBaseY = hasOutlier
        ? yScale.range()[1] - (padding.top + 12)
        : yScale(data[0][`new_${metricKey}`]);
      const firstCaseYPos = Math.min(firstCaseBaseY, yScale.range()[1] - padding.top);
      return (
        <g
          id='firstCase'
          style={{ pointerEvents: 'none' }}
          transform={`translate(${xScale(data[0].date)}, ${dimensions.height - (padding.bottom + padding.top + firstCaseYPos)})`}
          fontSize={hasOutlier ? text.medium : text.small}
        >
          {
            hasOutlier ? (
              <path d='M 4 0 L 0 4 L 4 8 Z' stroke='black' fill='black' transform={`translate(${-5.5}, ${12.5})`} /> // 6
              ) : (
              <line
                y1={4}
                y2={firstCaseBaseY < yScale.range()[1] - padding.top ? padding.top : 4}
                stroke='#C4C4C4'
              />
            )
          }
          {
            firstConfirmedTextWidth != null ? (
              <rect
                style={{ filter: hasOutlier ? null : 'url(#shadow)' }}
                fill={rectFill}
                stroke={hasOutlier ? 'black' : '#C4C4C4'}
                strokeWidth='0.5'
                x={-1}
                y={hasOutlier ? -1 : 0}
                width={
                  hasOutlier
                  ? (firstConfirmedTextWidth / dimensions.scale) + 10
                  : (firstConfirmedTextWidth / dimensions.scale) + 8
                }
                height={hasOutlier ? 36 : 30}
                rx={4}
              />
            ) : null
          }
          <g ref={firstConfirmedTextRef} textAnchor={isRTL ? 'end' : 'start'}>
            <text dx={4} dy={hasOutlier ? text.large : text.medium}>{firstConfirmedLines[0]}</text>
            <text dx={4} dy={hasOutlier ? text.large * 2 : text.medium * 2}>{firstConfirmedLines[1]}</text>
          </g>
        </g>
      );
    }
    return null;
  }, [data, firstConfirmedTextRef, firstConfirmedTextWidth, dimensions.height, dimensions.scale, isRTL, language, metricKey, text, xScale, yScale, outlierData, formatNumber]);

  const bars = useMemo(() => (
    data.map(day => {
      const value = yScale(day[`new_${metricKey}`]);
      const barHeight = Math.max(0, isNaN(value) ? 0 : value);
      const opacity = tooltipData === null ? 0.9 : (tooltipData.data === day ? 1 : 0.3)
      const maxBarPadding = 3
      const fullBarWidth = Math.floor(dimensions.barWidth * 2)
      let barPadding = maxBarPadding
      if (fullBarWidth < 6) {
        barPadding = 2
      }
      if (fullBarWidth < 4) {
        barPadding = 1
      }
      if (fullBarWidth < 3) {
        barPadding = 0.5
      }
      return (
        <g
          className='bar'
          key={formatDate(day.date, language)}
          transform={`translate(${xScale(day.date)}, 0)`}
          onMouseMove={event => hoverDay(event, day)}
          onMouseOut={() => setTooltipData(null)}
          opacity={opacity}
        >
          <rect
            height={yScale.range()[1]}
            x={-dimensions.barWidth}
            y={-yScale(yScale.domain()[1])}
            width={dimensions.barWidth * 2}
            fill='transparent'
          />
          <rect
            transform={`translate(${(-fullBarWidth + barPadding) / 2}, ${-barHeight})`}
            width={Math.max(1, fullBarWidth - barPadding)}
            height={barHeight}
            fill={props.barColor}
          />
        </g>
      )
    })
  ), [data, dimensions, hoverDay, language, metricKey, props.barColor, tooltipData, xScale, yScale]);

  const title = regionType === 'usa' ? 'the United States' : props.title;

  return (
    <div ref={graphRef} className='BarGraph'>
      {
        data.length ? (
          <svg width={dimensions.width * dimensions.scale} height={dimensions.height * dimensions.scale}>
            <g transform={`translate(1, 0) scale(${dimensions.scale})`}>
              <filter id='shadow'>
                <feDropShadow dx='1' dy='2' stdDeviation='1' floodColor='#4C4C4C'/>
              </filter>
              <rect width={dimensions.width} height={dimensions.height} fill='white' />
              <text
                id='title'
                fontSize={text.large}
                dy={text.large * 2.5}
                fontWeight='bold'
                dx={isRTL ? dimensions.width : 0}
              >
                {
                  regionType === 'world'
                    ? getText(language, metricKey === 'cases' ? 'dailyNewCasesGlobally' : 'dailyFatalitiesGlobally')
                    : getText(language, metricKey === 'cases' ? 'dailyNewCasesIn' : 'dailyFatalitiesIn')
                }
                {' '}
                {title}
              </text>
              <g id='xTicks' transform={`translate(0, ${dimensions.height - padding.bottom})`}>
                {xTicks}
              </g>
              <g id='yTicks' transform={`translate(0, ${dimensions.height - padding.bottom})`}>
                {yTicks}
              </g>
              <g id='bars' transform={`translate(0, ${dimensions.height - padding.bottom})`}>
                {bars}
              </g>
              {sevenDayAverageGroup}
              <g id='annotations'>
                {firstCase}
              </g>
            </g>
          </svg>
        ) : null
      }
      {
        // outlierData ? (
        //   <div style={{
        //     position: 'absolute',
        //     pointerEvents: 'none',
        //     transformOrigin: '0 0',
        //     transform: `translate(${32 + (padding.left * dimensions.scale)}px, ${padding.top * dimensions.scale}px)`,
        //     padding: '0.25em',
        //     backgroundColor: !props.isWorld
        //       ? '#A1A1A1'
        //       : metricKey === 'cases'
        //         ? 'rgba(136, 49, 67, 0.1)'
        //         : 'rgba(140, 153, 154, 0.1)',
        //   }}>
        //     {`${formatNumber(outlierData[metricKey])} reported ${getText(language, metricKey)} before ${formatDate(data[0].date, language)}`}
        //   </div>
        // ) : null
      }
      {
        tooltipData ? (
          <div
            className='tooltip'
            style={{
              left: `${tooltipData.position.x}px`,
              top: `${tooltipData.position.y}px`,
              transform: tooltipData.position.transform,
            }}
          >
            <div className='value'>
              {formatDate(tooltipData.data.date, language, true)}
            </div>
            <div className='label'>
              {getText(language, metricKey === 'cases' ? 'newCases' : 'newFatalities')}
            </div>
            <div className='value'>
              {formatNumber(tooltipData.data[`new_${metricKey}`])}
            </div>
            {
              tooltipData.data.priorWeek ? (
                <div>
                  <div className='label'>
                    {getText(language, 'changeFrom7Days')}
                  </div>
                  <div className='value'>
                    {
                      isFinite(tooltipData.percentChange)
                        ? `${tooltipData.percentChange > 0 ? '+' : ''}${formatPercentage(tooltipData.percentChange)}`
                        : 'n/a'
                    }
                  </div>
                  <div className='label'>
                    {getText(language, 'sevenDayAverage')}
                  </div>
                  <div className='value'>
                    {
                      isFinite(tooltipData.data[`new_${metricKey}_7dayAverage`])
                        ? formatNumber(Math.round(tooltipData.data[`new_${metricKey}_7dayAverage`]))
                        : 'n/a'
                    }
                  </div>
                </div>
              ) : null
            }
          </div>
        ) : null
      }
    </div>
  );
};
